/**
 * Модернізація ОЗ + Переміщення активів (Підпорція 1.3, 2026-05-25).
 * 2026-08-03 (Активи-UI етап 2b): додано створення документів з UI
 * (раніше list-only) — модалки + провести/розпровести на списку.
 */
import { useEffect, useMemo, useRef, useState } from 'react';
import { useNavigate, useSearchParams } from 'react-router-dom';
import { openPrintView } from '@/shared/utils/printView';
import { useMutation, useQuery, useQueryClient } from '@tanstack/react-query';
import {
  modernization,
  internalTransfer,
  type FixedAssetModernization,
  type AssetInternalTransfer,
  type InternalTransferItem,
  type AssetKind,
} from '@/api/asset_movement';
import { fixedAssets, intangibleAssets, lowValueAssets } from '@/api/assets';
import { departmentsApi } from '@/api/hr';
import { usePermissionsByOrg } from '@/hooks/usePermissionsByOrg';
import DataTable, { type ColumnDef } from '@/shared/components/DataTable';
import DocListFilters, { DocListFiltersRow, useDocListFilters } from '@/shared/components/DocListFilters';
import { useOrgNames } from '@/shared/hooks/useOrgNames';
import { useAttachmentFlags } from '@/shared/hooks/useAttachmentFlags';
import { useSignControlMulti } from '@/shared/hooks/useSignControl';
import TchTable from '@/shared/components/TchTable';


const fmt2 = (v: string | number) =>
  Number(v).toLocaleString('uk-UA', {
    minimumFractionDigits: 2, maximumFractionDigits: 2,
  });

const KIND_UA: Record<AssetKind, string> = {
  FIXED_ASSET: 'ОЗ', INTANGIBLE: 'НМА', LOW_VALUE: 'МНМА',
};

function errDetail(e: unknown): string {
  const d = (e as { response?: { data?: { detail?: unknown } } })?.response?.data?.detail;
  return typeof d === 'string' ? d : JSON.stringify(d ?? String(e));
}


// ========================= Модернізація: модалка ======================


function ModernizationModal({ onClose, initialAssetId, initialOrgId }: {
  onClose: () => void; initialAssetId?: string; initialOrgId?: string;
}) {
  const qc = useQueryClient();
  const { availableOrgs } = usePermissionsByOrg(
    'documents.fixed_asset_modernization.create');
  const [orgId, setOrgId] = useState(initialOrgId ?? '');
  const orgEff = orgId || availableOrgs[0]?.id || '';
  const [docDate, setDocDate] = useState(new Date().toISOString().slice(0, 10));
  const [faId, setFaId] = useState(initialAssetId ?? '');
  const [comment, setComment] = useState('');
  const [rows, setRows] = useState<Array<{
    description: string; amount: string; cost_account_code: string;
  }>>([{ description: '', amount: '', cost_account_code: '631' }]);
  const [error, setError] = useState<string | null>(null);

  const faQ = useQuery({
    queryKey: ['fixed-assets-v2', orgEff],
    queryFn: () => fixedAssets.list({ organization_id: orgEff, limit: 500 }),
    enabled: !!orgEff,
  });
  const faList = (faQ.data?.items ?? []).filter(
    (a) => ['OPERATING', 'PARKED', 'CONSERVATION', 'REPAIR_LONG'].includes(a.status));
  const selectedFa = faList.find((a) => a.id === faId);
  const total = rows.reduce((s, r) => s + (Number(r.amount) || 0), 0);
  const validRows = rows.filter(
    (r) => r.description.trim().length >= 3 && Number(r.amount) > 0);

  const createdId = useRef<string | null>(null);
  const save = useMutation({
    mutationFn: async (andPost: boolean) => {
      // ч.1343: якщо проведення відмовило, документ уже створено — повторне «провести»
      // оновлює ЙОГО (з виправленнями у вікні), а не створює другий такий самий.
      const body = {
        organization_id: orgEff,
        document_number: null,
        document_date: `${docDate}T12:00:00`,
        fixed_asset_id: faId,
        comment: comment || null,
        items: validRows.map((r, i) => ({
          line_number: i + 1,
          description: r.description.trim(),
          amount: r.amount,
          cost_account_code: r.cost_account_code || '631',
        })),
      };
      const doc = createdId.current
        ? await modernization.update(createdId.current, body)
        : await modernization.create(body);
      createdId.current = doc.id;
      if (andPost) await modernization.post(doc.id);
      return doc;
    },
    onSuccess: () => {
      qc.invalidateQueries({ queryKey: ['modernization'] });
      qc.invalidateQueries({ queryKey: ['fixed-assets-v2'] });
      onClose();
    },
    onError: (e: unknown) => setError(errDetail(e)),
  });

  return (
    <div style={{ position: 'fixed', inset: 0, zIndex: 1100, background: 'var(--overlay)',
                  display: 'flex', alignItems: 'center', justifyContent: 'center' }}
         onClick={onClose}>
      <div className="card" style={{ width: 720, maxHeight: '85vh', overflowY: 'auto', margin: 0 }}
           onClick={(e) => e.stopPropagation()}>
        <div className="toolbar" style={{ marginBottom: '0.5rem' }}>
          <h3 style={{ margin: 0 }}>Модернізація ОЗ</h3>
          <button style={{ marginLeft: 'auto' }} onClick={onClose}>закрити</button>
        </div>
        <div className="doc-grid">
          <div className="field" style={{ gridColumn: 'span 9' }}>
            <label>Організація</label>
            <select value={orgEff}
              onChange={(e) => { setOrgId(e.target.value); setFaId(''); }}>
              {availableOrgs.map((o) => (
                <option key={o.id} value={o.id}>{o.name}</option>
              ))}
            </select>
          </div>
          <div className="field" style={{ gridColumn: 'span 3' }}>
            <label>Дата</label>
            <input type="date" value={docDate}
              onChange={(e) => setDocDate(e.target.value)} />
          </div>
        </div>
        <div className="field">
          <label>Основний засіб *</label>
          <select value={faId} onChange={(e) => setFaId(e.target.value)}>
            <option value="">— оберіть ОЗ —</option>
            {faList.map((a) => (
              <option key={a.id} value={a.id}>
                {a.inventory_number} {a.name}
              </option>
            ))}
          </select>
        </div>
        {selectedFa && (
          <div style={{ color: 'var(--muted)', fontSize: '0.85rem', marginBottom: '0.5rem' }}>
            Первісна: {fmt2(selectedFa.primary_cost)} · Знос:{' '}
            {fmt2(selectedFa.accumulated_depreciation)} · Після проведення
            первісна зросте на {fmt2(total)}.
          </div>
        )}
        <h4 style={{ margin: '0.5rem 0 0.25rem' }}>Витрати на модернізацію</h4>
        <TchTable>
          <thead>
            <tr style={{ color: 'var(--muted)' }}>
              <th style={{ textAlign: 'left' }}>Опис (мін. 3 символи)</th>
              <th style={{ width: 130 }}>Сума</th>
              <th style={{ width: 110 }}>Кт рахунок</th>
              <th style={{ width: 30 }} />
            </tr>
          </thead>
          <tbody>
            {rows.map((r, i) => (
              <tr key={i}>
                <td>
                  <input value={r.description}
                    onChange={(e) => setRows((rs) =>
                      rs.map((x, idx) => idx === i ? { ...x, description: e.target.value } : x))} />
                </td>
                <td>
                  <input style={{ textAlign: 'right' }} value={r.amount}
                    onChange={(e) => setRows((rs) =>
                      rs.map((x, idx) => idx === i ? { ...x, amount: e.target.value } : x))} />
                </td>
                <td>
                  <input value={r.cost_account_code}
                    onChange={(e) => setRows((rs) =>
                      rs.map((x, idx) => idx === i ? { ...x, cost_account_code: e.target.value } : x))} />
                </td>
                <td>
                  <button className="danger"
                    onClick={() => setRows((rs) => rs.filter((_, idx) => idx !== i))}>×</button>
                </td>
              </tr>
            ))}
          </tbody>
        </TchTable>
        <button style={{ marginTop: '0.35rem' }}
          onClick={() => setRows((rs) =>
            [...rs, { description: '', amount: '', cost_account_code: '631' }])}>
          додати рядок
        </button>
        <div style={{ textAlign: 'right', fontWeight: 600, margin: '0.5rem 0' }}>
          Разом капіталізується: {fmt2(total)}
        </div>
        <div className="field">
          <label>Коментар</label>
          <input value={comment} onChange={(e) => setComment(e.target.value)} />
        </div>
        {error && <div style={{ color: '#dc2626', marginBottom: 8, whiteSpace: 'pre-wrap' }}>{error}</div>}
        <div style={{ display: 'flex', gap: '0.5rem' }}>
          <button className="primary"
            disabled={!faId || !validRows.length || save.isPending}
            onClick={() => save.mutate(true)}>
            {save.isPending ? 'виконується…' : 'провести'}
          </button>
          <button disabled={!faId || !validRows.length || save.isPending}
            onClick={() => save.mutate(false)}>
            зберегти чернетку
          </button>
          <button onClick={onClose}>скасувати</button>
        </div>
      </div>
    </div>
  );
}


export function ModernizationPage() {
  const qc = useQueryClient();
  const [sp] = useSearchParams();
  const [createOpen, setCreateOpen] = useState(!!sp.get('asset_id'));
  const list = useQuery({
    queryKey: ['modernization'],
    queryFn: () => modernization.list({ limit: 200 }),
  });
  const attF = useAttachmentFlags('doc.fixed_asset_modernization', list.data?.items ?? []);
  const scF = useSignControlMulti('doc.fixed_asset_modernization', list.data?.items ?? []);
  // Резолв назви активу (раніше колонка показувала UUID).
  const faAllQ = useQuery({
    queryKey: ['fixed-assets-v2', 'all'],
    queryFn: () => fixedAssets.list({ limit: 1000 }),
  });
  const faName = (id: string) => {
    const a = (faAllQ.data?.items ?? []).find((x) => x.id === id);
    return a ? `${a.inventory_number} ${a.name}` : id.slice(0, 8) + '…';
  };
  const postMut = useMutation({
    mutationFn: ({ id, posted }: { id: string; posted: boolean }) =>
      posted ? modernization.unpost(id) : modernization.post(id),
    onSuccess: () => {
      qc.invalidateQueries({ queryKey: ['modernization'] });
      qc.invalidateQueries({ queryKey: ['fixed-assets-v2'] });
    },
    onError: (e: unknown) => alert(errDetail(e)),
  });
  const cols = useMemo<ColumnDef<FixedAssetModernization>[]>(
    () => [
      { key: 'document_number', header: '№', width: 150, importBadge: true,
        sortValue: (r) => r.document_number ?? '', cell: (r) => r.document_number },
      { key: 'document_date', header: 'Дата', width: 170,
        sortValue: (r) => r.document_date ?? '',
        cell: (r) => (r.document_date ? new Date(r.document_date).toLocaleString('uk-UA') : '') },
      { key: 'fixed_asset_id', header: 'Актив',
        sortValue: (r) => faName(r.fixed_asset_id),
        cell: (r) => faName(r.fixed_asset_id) },
      { key: 'primary_cost_before', header: 'Вартість до', width: 150, align: 'right',
        sortValue: (r) => Number(r.primary_cost_before), cell: (r) => fmt2(r.primary_cost_before) },
      { key: 'total_capitalized', header: 'Капіталізовано', width: 160, align: 'right',
        sortValue: (r) => Number(r.total_capitalized), cell: (r) => fmt2(r.total_capitalized) },
      { key: 'posted', header: 'Проведено', width: 120, align: 'center',
        sortValue: (r) => (r.posted ? 1 : 0), cell: (r) => (r.posted ? '✓ так' : 'ні') },
      ...attF.column,
      ...scF.column,
      {
        key: 'actions', header: '', width: 130, align: 'center',
        cell: (r) => (
          <button style={{ padding: '0.1rem 0.5rem', fontSize: '0.78rem' }}
            disabled={postMut.isPending}
            onClick={(e) => { e.stopPropagation(); postMut.mutate({ id: r.id, posted: r.posted }); }}>
            {r.posted ? 'розпровести' : 'провести'}
          </button>
        ),
      },
    ],
    // eslint-disable-next-line react-hooks/exhaustive-deps
    [postMut, faAllQ.data],
  );
  return (
    <div className="card">
      <div className="toolbar">
        <h2 style={{ margin: 0 }}>Модернізація ОЗ</h2>
        <button className="primary" style={{ marginLeft: 'auto' }}
          onClick={() => setCreateOpen(true)}>
          + Новий документ
        </button>
        <span style={{ color: '#666' }}>
          Усього: {list.data?.total ?? 0}
        </span>
      </div>
      <DataTable<FixedAssetModernization>
        rows={list.data?.items ?? []}
        columns={cols}
        rowKey={(r) => r.id}
        loading={list.isLoading}
        emptyText="Немає документів."
        initialSortKey="document_date"
        initialSortDir="desc"
      />
      {createOpen && <ModernizationModal onClose={() => setCreateOpen(false)}
        initialAssetId={sp.get('asset_id') ?? undefined} initialOrgId={sp.get('organization_id') ?? undefined} />}
    </div>
  );
}


// ========================= Переміщення: модалка =======================


function TransferModal({ onClose, initialAssetId, initialOrgId }: {
  onClose: () => void; initialAssetId?: string; initialOrgId?: string;
}) {
  const qc = useQueryClient();
  const { availableOrgs } = usePermissionsByOrg(
    'documents.asset_internal_transfer.create');
  const [orgId, setOrgId] = useState(initialOrgId ?? '');
  const orgEff = orgId || availableOrgs[0]?.id || '';
  const [docDate, setDocDate] = useState(new Date().toISOString().slice(0, 10));
  const [reason, setReason] = useState('');
  const [comment, setComment] = useState('');
  const [rows, setRows] = useState<InternalTransferItem[]>([]);
  const [error, setError] = useState<string | null>(null);

  const faQ = useQuery({ queryKey: ['fixed-assets-v2', orgEff],
    queryFn: () => fixedAssets.list({ organization_id: orgEff, limit: 500 }),
    enabled: !!orgEff });
  const iaQ = useQuery({ queryKey: ['intangible-assets-v2', orgEff],
    queryFn: () => intangibleAssets.list({ organization_id: orgEff, limit: 500 }),
    enabled: !!orgEff });
  const lvQ = useQuery({ queryKey: ['low-value-assets', orgEff],
    queryFn: () => lowValueAssets.list({ organization_id: orgEff, limit: 500 }),
    enabled: !!orgEff });
  const depsQ = useQuery({ queryKey: ['departments', orgEff],
    queryFn: () => departmentsApi.list({ organization_id: orgEff, limit: 500 }),
    enabled: !!orgEff });
  const deps = depsQ.data?.items ?? [];
  const depName = (id?: string | null) =>
    deps.find((d) => d.id === id)?.name ?? '—';

  const used = new Set(rows.map((r) =>
    r.fixed_asset_id ?? r.intangible_asset_id ?? r.low_value_asset_id));
  const candidates = [
    ...(faQ.data?.items ?? []).map((a) => ({ kind: 'FIXED_ASSET' as AssetKind, a })),
    ...(iaQ.data?.items ?? []).map((a) => ({ kind: 'INTANGIBLE' as AssetKind, a })),
    ...(lvQ.data?.items ?? []).map((a) => ({ kind: 'LOW_VALUE' as AssetKind, a })),
  ].filter(({ a }) =>
    ['OPERATING', 'PARKED', 'CONSERVATION'].includes(a.status) && !used.has(a.id));

  const allAssets = [
    ...(faQ.data?.items ?? []), ...(iaQ.data?.items ?? []),
    ...(lvQ.data?.items ?? [])];
  // Prefill з картки активу (2026-08-27).
  const [prefilled, setPrefilled] = useState(false);
  useEffect(() => {
    if (!initialAssetId || prefilled) return;
    const c = candidates.find(({ a }) => a.id === initialAssetId);
    if (!c) return;
    const { kind, a } = c;
    setRows((rs) => [...rs, {
      line_number: rs.length + 1, asset_kind: kind,
      fixed_asset_id: kind === 'FIXED_ASSET' ? a.id : null,
      intangible_asset_id: kind === 'INTANGIBLE' ? a.id : null,
      low_value_asset_id: kind === 'LOW_VALUE' ? a.id : null,
      from_department_id: ('department_id' in a ? a.department_id : null) ?? null,
      to_department_id: null, from_employee_id: null, to_employee_id: null, item_reason: null,
    }]);
    setPrefilled(true);
  });

  const createdId = useRef<string | null>(null);
  const save = useMutation({
    mutationFn: async (andPost: boolean) => {
      // ч.1343: якщо проведення відмовило, документ уже створено — повторне «провести»
      // оновлює ЙОГО (з виправленнями у вікні), а не створює другий такий самий.
      const body = {
        organization_id: orgEff,
        document_number: null,
        document_date: `${docDate}T12:00:00`,
        transfer_reason: reason.trim(),
        comment: comment || null,
        items: rows.map((r, i) => ({ ...r, line_number: i + 1 })),
      };
      const doc = createdId.current
        ? await internalTransfer.update(createdId.current, body)
        : await internalTransfer.create(body);
      createdId.current = doc.id;
      if (andPost) await internalTransfer.post(doc.id);
      return doc;
    },
    onSuccess: () => {
      qc.invalidateQueries({ queryKey: ['internal-transfer'] });
      qc.invalidateQueries({ queryKey: ['fixed-assets-v2'] });
      qc.invalidateQueries({ queryKey: ['intangible-assets-v2'] });
      qc.invalidateQueries({ queryKey: ['low-value-assets'] });
      onClose();
    },
    onError: (e: unknown) => setError(errDetail(e)),
  });

  const rowsValid = rows.length > 0
    && rows.every((r) => r.to_department_id);

  return (
    <div style={{ position: 'fixed', inset: 0, zIndex: 1100, background: 'var(--overlay)',
                  display: 'flex', alignItems: 'center', justifyContent: 'center' }}
         onClick={onClose}>
      <div className="card" style={{ width: 820, maxHeight: '85vh', overflowY: 'auto', margin: 0 }}
           onClick={(e) => e.stopPropagation()}>
        <div className="toolbar" style={{ marginBottom: '0.5rem' }}>
          <h3 style={{ margin: 0 }}>Переміщення активів (ОЗ-3)</h3>
          <button style={{ marginLeft: 'auto' }} onClick={onClose}>закрити</button>
        </div>
        <div className="doc-grid">
          <div className="field" style={{ gridColumn: 'span 9' }}>
            <label>Організація</label>
            <select value={orgEff}
              onChange={(e) => { setOrgId(e.target.value); setRows([]); }}>
              {availableOrgs.map((o) => (
                <option key={o.id} value={o.id}>{o.name}</option>
              ))}
            </select>
          </div>
          <div className="field" style={{ gridColumn: 'span 3' }}>
            <label>Дата</label>
            <input type="date" value={docDate}
              onChange={(e) => setDocDate(e.target.value)} />
          </div>
        </div>
        <div className="field">
          <label>Причина переміщення * (мін. 3 символи)</label>
          <input value={reason} onChange={(e) => setReason(e.target.value)} />
        </div>
        <h4 style={{ margin: '0.5rem 0 0.25rem' }}>Активи ({rows.length})</h4>
        <TchTable>
          {rows.length > 0 && (
            <thead>
              <tr style={{ color: 'var(--muted)' }}>
                <th style={{ width: 55 }} />
                <th style={{ textAlign: 'left' }}>Актив</th>
                <th style={{ textAlign: 'left', width: 150 }}>Звідки</th>
                <th style={{ textAlign: 'left', width: 180 }}>Куди *</th>
                <th style={{ width: 30 }} />
              </tr>
            </thead>
          )}
          <tbody>
            {rows.map((r, i) => {
              const a = allAssets.find((x) => x.id ===
                (r.fixed_asset_id ?? r.intangible_asset_id ?? r.low_value_asset_id));
              return (
                <tr key={i} style={{ borderBottom: '1px solid var(--border-soft)' }}>
                  <td>{KIND_UA[r.asset_kind]}</td>
                  <td>
                    {a ? `${a.inventory_number} ${a.name}` : '—'}
                  </td>
                  <td style={{ color: 'var(--muted)' }}>
                    {depName(r.from_department_id)}
                  </td>
                  <td>
                    <select style={{ width: '100%' }} value={r.to_department_id ?? ''}
                      onChange={(e) => setRows((rs) => rs.map((x, idx) =>
                        idx === i ? { ...x, to_department_id: e.target.value || null } : x))}>
                      <option value="">— підрозділ —</option>
                      {deps.map((d) => (
                        <option key={d.id} value={d.id}>{d.name}</option>
                      ))}
                    </select>
                  </td>
                  <td>
                    <button className="danger" style={{ padding: '0.1rem 0.45rem' }}
                      onClick={() => setRows((rs) => rs.filter((_, idx) => idx !== i))}>×</button>
                  </td>
                </tr>
              );
            })}
          </tbody>
        </TchTable>
        <h4 style={{ margin: '0.75rem 0 0.25rem' }}>Додати актив</h4>
        <div style={{ maxHeight: 200, overflowY: 'auto', border: '1px solid var(--border-soft)', borderRadius: 6 }}>
          <table className="tch">
            <tbody>
              {candidates.length === 0 && (
                <tr><td style={{ color: 'var(--muted)' }}>
                  Немає доступних активів.
                </td></tr>
              )}
              {candidates.map(({ kind, a }) => (
                <tr key={a.id} style={{ cursor: 'pointer', borderBottom: '1px solid var(--border-soft)' }}
                    onClick={() => setRows((rs) => [...rs, {
                      line_number: rs.length + 1,
                      asset_kind: kind,
                      fixed_asset_id: kind === 'FIXED_ASSET' ? a.id : null,
                      intangible_asset_id: kind === 'INTANGIBLE' ? a.id : null,
                      low_value_asset_id: kind === 'LOW_VALUE' ? a.id : null,
                      from_department_id:
                        ('department_id' in a ? a.department_id : null) ?? null,
                      to_department_id: null,
                      from_employee_id: null,
                      to_employee_id: null,
                      item_reason: null,
                    }])}
                    onMouseEnter={(e) => { e.currentTarget.style.background = 'var(--bg-soft)'; }}
                    onMouseLeave={(e) => { e.currentTarget.style.background = ''; }}>
                  <td style={{ width: 55 }}>{KIND_UA[kind]}</td>
                  <td>{a.inventory_number} {a.name}</td>
                  <td style={{ color: 'var(--muted)', width: 170 }}>
                    {depName(('department_id' in a ? a.department_id : null) ?? null)}
                  </td>
                </tr>
              ))}
            </tbody>
          </table>
        </div>
        <div className="field" style={{ marginTop: '0.5rem' }}>
          <label>Коментар</label>
          <input value={comment} onChange={(e) => setComment(e.target.value)} />
        </div>
        {error && <div style={{ color: '#dc2626', marginBottom: 8, whiteSpace: 'pre-wrap' }}>{error}</div>}
        <div style={{ display: 'flex', gap: '0.5rem' }}>
          <button className="primary"
            disabled={reason.trim().length < 3 || !rowsValid || save.isPending}
            onClick={() => save.mutate(true)}>
            {save.isPending ? 'виконується…' : 'провести'}
          </button>
          <button disabled={reason.trim().length < 3 || !rowsValid || save.isPending}
            onClick={() => save.mutate(false)}>
            зберегти чернетку
          </button>
          <button onClick={onClose}>скасувати</button>
        </div>
      </div>
    </div>
  );
}


export function InternalTransferPage() {
  const navigate = useNavigate();
  const qc = useQueryClient();
  const [sp] = useSearchParams();
  const [createOpen, setCreateOpen] = useState(!!sp.get('asset_id'));
  const f = useDocListFilters();
  const orgNames = useOrgNames();
  const list = useQuery({
    queryKey: ['internal-transfer', ...f.queryKey],
    queryFn: () => internalTransfer.list({
      organization_id: f.orgId || undefined,
      posted: f.status === 'posted' ? true : f.status === 'draft' ? false : undefined,
      date_from: f.dateFrom, date_to: f.dateTo, limit: 500,
    }),
  });
  const attF = useAttachmentFlags('doc.asset_internal_transfer', list.data?.items ?? []);
  const scF = useSignControlMulti('doc.asset_internal_transfer', list.data?.items ?? []);
  const postMut = useMutation({
    mutationFn: ({ id, posted }: { id: string; posted: boolean }) =>
      posted ? internalTransfer.unpost(id) : internalTransfer.post(id),
    onSuccess: () => {
      qc.invalidateQueries({ queryKey: ['internal-transfer'] });
      qc.invalidateQueries({ queryKey: ['fixed-assets-v2'] });
    },
    onError: (e: unknown) => alert(errDetail(e)),
  });
  const cols = useMemo<ColumnDef<AssetInternalTransfer>[]>(
    () => [
      { key: 'document_number', header: '№', width: 150, importBadge: true,
        sortValue: (r) => r.document_number ?? '', cell: (r) => r.document_number },
      { key: 'document_date', header: 'Дата', width: 170,
        sortValue: (r) => r.document_date ?? '',
        cell: (r) => (r.document_date ? new Date(r.document_date).toLocaleString('uk-UA') : '') },
      { key: 'organization_id', header: 'Організація', width: 190,
        sortValue: (r) => orgNames.get(r.organization_id) ?? '', cell: (r) => orgNames.get(r.organization_id) ?? '' },
      { key: 'transfer_reason', header: 'Причина',
        sortValue: (r) => r.transfer_reason ?? '', cell: (r) => r.transfer_reason },
      { key: 'items', header: 'Позицій', width: 110, align: 'right',
        sortValue: (r) => r.items?.length ?? 0, cell: (r) => r.items?.length ?? 0 },
      { key: 'posted', header: 'Проведено', width: 120, align: 'center',
        sortValue: (r) => (r.posted ? 1 : 0), cell: (r) => (r.posted ? '✓ так' : 'ні') },
      ...attF.column,
      ...scF.column,
      {
        key: 'actions', header: '', width: 200, align: 'center',
        cell: (r) => (
          <span style={{ display: 'inline-flex', gap: '0.35rem' }}>
            <button style={{ padding: '0.1rem 0.5rem', fontSize: '0.78rem' }}
              disabled={postMut.isPending}
              onClick={(e) => { e.stopPropagation(); postMut.mutate({ id: r.id, posted: r.posted }); }}>
              {r.posted ? 'розпровести' : 'провести'}
            </button>
            {r.posted && (
              <button style={{ padding: '0.1rem 0.5rem', fontSize: '0.78rem' }}
                onClick={(e) => {
                  e.stopPropagation();
                  void openPrintView(internalTransfer.printViewUrl(r.id));
                }}>
                друк
              </button>
            )}
          </span>
        ),
      },
    ],
    [postMut, orgNames, attF.column, scF.column],
  );
  return (
    <div className="card">
      <div className="toolbar">
        <h2 style={{ margin: 0 }}>Переміщення активів (ОЗ-3)</h2>
        <button className="primary" style={{ marginLeft: 'auto' }}
          onClick={() => navigate('/assets/transfer/new')}>
          + Новий документ
        </button>
      </div>
      <DocListFiltersRow>
        <DocListFilters f={f} statusOptions={[
          { value: '', label: 'Усі статуси' },
          { value: 'posted', label: 'Проведені' },
          { value: 'draft', label: 'Чернетки' },
        ]} />
      </DocListFiltersRow>
      <DataTable<AssetInternalTransfer>
        onRowDoubleClick={(r) => navigate(`/assets/transfer/${r.id}`)}
        rows={list.data?.items ?? []}
        columns={cols}
        rowKey={(r) => r.id}
        loading={list.isLoading}
        emptyText="Немає документів."
        initialSortKey="document_date"
        initialSortDir="desc"
      />
      {createOpen && <TransferModal onClose={() => setCreateOpen(false)}
        initialAssetId={sp.get('asset_id') ?? undefined} initialOrgId={sp.get('organization_id') ?? undefined} />}
    </div>
  );
}
